import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export interface ClinicUserGroupAuditLog {
  clinic_user_group_id: number;
  api_metadata: any;
  group_state_new: any;
  group_state_old: any;
  mapper_changes: any;
  action_type?: string;
  action_datetime?: string;
}

const CLINIC_USER_GROUP_AUDIT_LOG_QUERY = `
  query GetClinicUserGroupAuditLog($clinicUserGroupId: bigint!) {
    pep_clinic_user_group_audit_log(where: {clinic_user_group_id: {_eq: $clinicUserGroupId}}) {
      clinic_user_group_id
      api_metadata
      group_state_new
      group_state_old
      mapper_changes
    }
  }
`;

export const useClinicUserGroupAuditLog = (clinicUserGroupId: number | null) => {
  const { hasuraToken } = useAuth();
  const { handleError } = useErrorHandler();
  const [auditLogs, setAuditLogs] = useState<ClinicUserGroupAuditLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (!clinicUserGroupId || !hasuraToken) {
        setAuditLogs([]);
        return;
      }

      setLoading(true);
      try {
        const result = await graphqlClient.query<{
          pep_clinic_user_group_audit_log: ClinicUserGroupAuditLog[];
        }>(
          CLINIC_USER_GROUP_AUDIT_LOG_QUERY,
          { clinicUserGroupId },
          hasuraToken
        );

        if (result.success && result.data) {
          setAuditLogs(result.data.pep_clinic_user_group_audit_log || []);
        } else {
          handleError(result.errors?.[0] || 'Failed to fetch audit logs');
          setAuditLogs([]);
        }
      } catch (err) {
        handleError(err, 'Error fetching clinic user group audit logs');
        setAuditLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [clinicUserGroupId, hasuraToken]);

  return { auditLogs, loading };
};
